import React, { useEffect, useRef } from "react"
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, Flex, Button, Heading, Text, Box } from "@chakra-ui/react"

import { useAppContext } from "@app/providers/app"
import { useMedia } from "@app/hooks/useMedia"
import { useNewsletter } from "@app/hooks/useNewsletter"
import { useCustomerDetails } from "@app/hooks/useCustomer"

import SubscribeFormDetailed from "@app/components/Subscribe/SubscribeFormDetailed"
import ResolvedImageResponsive from "@app/components/ResolvedImageResponsive"
import { useColorPreset } from "@app/hooks/useColorPreset"

import type { Location } from "@root/types/global"
import { useAnalytics } from "@app/hooks/useAnalytics"

type Props = {
  success: boolean
  setSuccess: (value: boolean) => void
  location: Location
}

const SubscribePopup: React.FC<Props> = ({ success, setSuccess, location }) => {
  const { activeSubscribe, newsletter, handleClose } = useNewsletter(location)
  const initialRef = useRef(null)
  const title = newsletter?.additionalTitle
  const paragraph = newsletter?.additionalParagraph
  const { isBase } = useMedia()
  const {
    state: { geolocationOpen },
  } = useAppContext()
  const { customer } = useCustomerDetails()

  const { color: resolvedBgColor } = useColorPreset(newsletter?.backgroundColor)
  const successText = newsletter?.additionalSuccess || "Thank you for subscribing!"
  const successText2 = newsletter?.additionalSuccess1 || ""

  const { trackSubscriptionPopupView } = useAnalytics()
  const hasTrackedViewRef = useRef(false)

  useEffect(() => {
    if (activeSubscribe && !geolocationOpen && !hasTrackedViewRef.current) {
      trackSubscriptionPopupView()
      hasTrackedViewRef.current = true
    }
  }, [activeSubscribe, geolocationOpen, trackSubscriptionPopupView])

  return (
    !customer?.acceptsMarketing && (
      <Modal
        onClose={handleClose}
        isOpen={activeSubscribe && !geolocationOpen}
        initialFocusRef={initialRef}
        motionPreset="slideInBottom"
        scrollBehavior={"outside"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW={["92%", "882px"]} borderRadius="20px" w="auto">
          <ModalCloseButton margin="5px" zIndex={9} />
          <ModalBody p={0} bgColor={resolvedBgColor || "grey.lightest"} borderRadius="20px">
            <Box border="8px solid transparent">
              {!success ? (
                <Flex
                  flexDirection={{ base: "column", md: "row-reverse" }}
                  alignItems="center"
                  p="0"
                  gap={{ base: "0", md: "22px" }}
                  justifyContent="space-evenly"
                  borderRadius="20px"
                  overflow="hidden"
                  bg={resolvedBgColor}
                >
                  {newsletter?.image && (
                    <Box pos="relative" width={{ base: "100%", md: "412px" }} minWidth={{ base: "100%", md: "412px" }}>
                      <ResolvedImageResponsive image={newsletter?.image} w="100%" ratio={{ base: 330 / 212, md: 412 / 678 }} />
                    </Box>
                  )}
                  <Flex
                    px={{ base: 6, md: 8 }}
                    py={{ base: 4, md: 8 }}
                    flexDir="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                  >
                    <Heading as="h2" size="h4" variant="bold" mb={4}>
                      {title}
                    </Heading>
                    {!isBase && (
                      <Heading size="h6" variant="medium" mb={4}>
                        {paragraph}
                      </Heading>
                    )}
                    <SubscribeFormDetailed {...{ success, setSuccess }} location={location} />
                  </Flex>
                </Flex>
              ) : (
                <Flex p={14} bg={resolvedBgColor} flexDir="column" borderRadius="20px" textAlign="center">
                  <Heading as="h2" size="h5" pb={2} fontWeight="medium">
                    {successText}
                  </Heading>
                  <Text as="p">{successText2}</Text>
                  <Button variant="secondary" size="large" onClick={handleClose} mx="auto" width="full" mt={8}>
                    Start Shopping
                  </Button>
                </Flex>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  )
}

export default React.memo(SubscribePopup)
